/**
 * «Новый процесс» (/processes/new) — выбор вида заявки. Отдельный маршрут вместо вкладки списка
 * (макет 16.10.2026): без счётчиков и вкладок, с поиском по названию/описанию/синонимам (`kw` в
 * реестре) и группами HR / Юридические / Общие (заказчик, 22.09.2026). Живые виды (`id in FORMS`)
 * — карточками по сетке, неживые — в свёрнутом блоке «Скоро появятся». Карточка открывает форму
 * подмаршрутом /processes/new/[form], поэтому «назад» и прямая ссылка работают.
 */
import { useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../../src/i18n'
import { useResponsive } from '../../../../src/lib/responsive'
import { matchesType } from '../../../../src/features/edo/registry'
import { CREATE_TYPES, FORMS, GROUPS } from '../../../../src/features/edo/createTypes'
import { Breadcrumb } from '../../../../src/features/edo/components/Breadcrumb'
import { ProcessTypeTile } from '../../../../src/features/edo/components/ProcessTypeTile'
import { Button, Card, Icon, SearchField, Tag } from '../../../../src/ui'
import { pressable } from '../../../../src/ui/primitives'
import { Page } from '../../../../src/ui/Screen'

export default function NewProcessScreen() {
  const { l, t } = useApp()
  const { width } = useResponsive()
  const [query, setQuery] = useState('')
  const [soonOpen, setSoonOpen] = useState(false)

  // Сетка без растягивания одиночной карточки: колонка = доля ширины, а не flexGrow. Ширина
  // карточки ≈ (окно − сайдбар 236 − поля 48) / колонок, не уже ~260 (как в ProcessRow — по окну).
  const cols = width >= 1180 ? 3 : width >= 820 ? 2 : 1

  const found = CREATE_TYPES.filter((pt) => matchesType(pt, query, t))
  const live = found.filter((pt) => pt.id in FORMS)
  const soon = found.filter((pt) => !(pt.id in FORMS))
  // Поиск нашёл только «скорые» — раскрываем блок сам, иначе находку не видно.
  const soonShown = soonOpen || (!!query.trim() && !live.length && !!soon.length)

  return (
    <Page>
      <Breadcrumb items={[{ label: l('proc.title'), href: '/processes' }, { label: l('doc.new') }]} />

      <XStack alignItems="flex-end" justifyContent="space-between" gap={16} flexWrap="wrap">
        <YStack gap={4} flexGrow={1} flexShrink={1} flexBasis={360}>
          <Text fontSize={26} fontWeight="800" color="$color" letterSpacing={-0.4} role="heading">
            {l('proc.newTitle')}
          </Text>
          <Text fontSize={14} color="$muted">
            {l('proc.newHint')}
          </Text>
        </YStack>
        <XStack flexGrow={0} flexShrink={1} flexBasis={420} minWidth={260}>
          <SearchField value={query} onChange={setQuery} placeholder={l('proc.newSearchPh')} label={l('proc.newSearch')} height={44} />
        </XStack>
      </XStack>

      {GROUPS.map((g) => {
        const items = live.filter((pt) => pt.group === g)
        if (!items.length) return null
        return (
          <YStack key={g} gap={12}>
            <XStack alignItems="baseline" columnGap={10} flexWrap="wrap">
              <Text fontSize={15} fontWeight="700" color="$color" role="heading">
                {l(`proc.group.${g}`)}
              </Text>
              <Text fontSize={12} color="$muted">
                {l(`proc.groupHint.${g}`)}
              </Text>
            </XStack>
            <XStack flexWrap="wrap" margin={-7}>
              {items.map((pt) => (
                <Stack key={pt.id} width={`${100 / cols}%`} padding={7}>
                  <ProcessTypeTile pt={pt} />
                </Stack>
              ))}
            </XStack>
          </YStack>
        )
      })}

      {query.trim() && !live.length ? (
        <Card padding={32} alignItems="center" gap={10}>
          <Text fontSize={15} fontWeight="700" color="$color">
            {l('proc.noSuchProcess')}
          </Text>
          <Text fontSize={13} color="$muted" textAlign="center" maxWidth={480}>
            {l('proc.noSuchHint')}
          </Text>
          <Button marginTop={6} height={44} onPress={() => setQuery('')}>
            {l('proc.clearSearch')}
          </Button>
        </Card>
      ) : null}

      {soon.length ? (
        <YStack gap={12} paddingTop={8} borderTopWidth={1} borderColor="$borderSecondary">
          <XStack
            {...pressable}
            onPress={() => setSoonOpen(!soonShown)}
            aria-expanded={soonShown}
            cursor="pointer"
            alignSelf="flex-start"
            alignItems="center"
            gap={8}
            minHeight={44}
            hoverStyle={{ opacity: 0.8 }}
          >
            <Stack rotate={soonShown ? '90deg' : '0deg'} animation="quick">
              <Icon name="chevron-right" size={18} color="$muted" />
            </Stack>
            <Text fontSize={13} fontWeight="600" color="$muted">
              {l('proc.soon')} · {soon.length}
            </Text>
          </XStack>
          {soonShown ? (
            <XStack flexWrap="wrap" margin={-5}>
              {soon.map((pt) => (
                <Stack key={pt.id} width={`${100 / cols}%`} padding={5}>
                  <XStack flexGrow={1} alignItems="center" gap={12} padding={12} borderRadius={7} borderWidth={1} borderStyle="dashed" borderColor="$borderColor">
                    <Icon name={pt.icon} size={18} color="$tertiary" />
                    <YStack flex={1} minWidth={0} gap={2}>
                      <Text fontSize={13} fontWeight="600" color="$muted">
                        {t(pt.name)}
                      </Text>
                      <Text fontSize={11} color="$tertiary">
                        {t(pt.desc)}
                      </Text>
                    </YStack>
                    <Tag>{l('proc.soonTag')}</Tag>
                  </XStack>
                </Stack>
              ))}
            </XStack>
          ) : null}
        </YStack>
      ) : null}
    </Page>
  )
}
